import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ClearIndicatorProps,
  components,
  MultiValue,
  MultiValueProps,
  MultiValueRemoveProps,
  OptionProps,
} from "react-select";
import CreatableSelect from "react-select/creatable";
import { ROLES_TO_TEXT } from "common/utils/users";
import {
  User,
  useSearchPublicUsersLazyQuery,
  useSearchPublicUsersQuery,
  useSearchUsersLazyQuery,
  useSearchUsersQuery,
} from "../../generated/graphql";
import { useIsMobileWidth } from "../Guest/utils";
import Wrapper from "../Inputs/Wrapper";
import {
  OptionContainer,
  OptionHeader,
  OptionSecondaryText,
  SelectAllSeparator,
  styledCreatableSelect,
} from "./__styles__/UserSelect";
import { Icon } from "./Icons/LucideIcons";
import { Body } from "./Typography";

export interface UserOption
  extends Pick<User, "id" | "firstName" | "lastName" | "email"> {
  role: { name: string };
  _isOverflowChip?: boolean;
  _userType?: "user" | "guest";
}

export type EntitySelectionOutput = Record<string, string[]>;

const SELECT_ALL_VALUE = "__SELECT_ALL__";

const CustomOption = (props: OptionProps<UserOption, true>) => {
  const option = props.data;

  if (option.id === SELECT_ALL_VALUE) {
    return (
      <components.Option {...props}>
        <OptionContainer>
          <Body size="large" type="regular">
            Select All
          </Body>
        </OptionContainer>
        <SelectAllSeparator />
      </components.Option>
    );
  }

  return (
    <components.Option {...props}>
      <OptionContainer>
        <OptionHeader>
          {`${option.firstName} ${option.lastName}`}
          {option?.role && (
            <OptionSecondaryText size="large" type="regular">
              {ROLES_TO_TEXT[option.role.name as keyof typeof ROLES_TO_TEXT] ||
                option.role.name}
            </OptionSecondaryText>
          )}
        </OptionHeader>
        <OptionSecondaryText size="large" type="regular">
          {option.email}
        </OptionSecondaryText>
      </OptionContainer>
    </components.Option>
  );
};

const formatValue = (option: UserOption) => {
  if (option.id === SELECT_ALL_VALUE) {
    return "Select All";
  }
  return `${option.firstName} ${option.lastName}`;
};

const CustomMultiValue = (
  props: MultiValueProps<UserOption, true> & {
    menuIsOpen: boolean;
    maxSelectedUsersToDisplay: number;
  }
) => {
  const { getValue, menuIsOpen, maxSelectedUsersToDisplay } = props;
  const chipPosition = (props as any).index as number;
  const totalSelectedUsers = getValue();
  const overflow = totalSelectedUsers.length - maxSelectedUsersToDisplay;

  if (!menuIsOpen && chipPosition > maxSelectedUsersToDisplay) {
    return null;
  }

  const overflowChip =
    !menuIsOpen && chipPosition === maxSelectedUsersToDisplay;
  const content = overflowChip ? `+${overflow}` : formatValue(props.data);

  const newProps = {
    ...props,
    data: {
      ...props.data,
      _isOverflowChip: overflowChip,
    },
  } as MultiValueProps<UserOption, true>;

  return (
    <components.MultiValue {...newProps}>
      <Body size="small" type="emphasis">
        {content}
      </Body>
    </components.MultiValue>
  );
};

const MultiValueRemove = (props: MultiValueRemoveProps<UserOption, true>) => {
  if (props.data._isOverflowChip) return null;

  return (
    <components.MultiValueRemove {...props}>
      <Icon iconName="x" color="contentSecondary" size={12} />
    </components.MultiValueRemove>
  );
};

const ClearIndicator = (props: ClearIndicatorProps<UserOption, true>) => {
  return (
    <components.ClearIndicator {...props}>
      <Icon iconName="x" color="contentDisabled" size={16} />
    </components.ClearIndicator>
  );
};

export type UserSelectProps = {
  name: string;
  value: EntitySelectionOutput;
  onChange: (value: Maybe<EntitySelectionOutput>) => void;
  disabled?: boolean;
  tooltip?: string;
  helperText?: string;
  placeholder?: string;
  label?: string;
  required?: boolean;
  error?: string;
  includeUsers?: boolean;
  includePublicUsers?: boolean;
  groups?: string[];
};

const UserSelect = ({
  name,
  value,
  onChange,
  disabled,
  tooltip,
  helperText,
  placeholder,
  label,
  required,
  error,
  includeUsers = true,
  includePublicUsers = false,
  groups = [],
  ...props
}: UserSelectProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUsers, setSelectedUsers] = useState<UserOption[]>([]);
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const isMobileWidth = useIsMobileWidth();
  const maxSelectedUsersToDisplay = isMobileWidth ? 1 : 3;

  const { data: usersData, loading: usersLoading } = useSearchUsersQuery({
    variables: { query: searchTerm },
    skip: !includeUsers,
  });

  const { data: publicUsersData, loading: publicUsersLoading } =
    useSearchPublicUsersQuery({
      variables: {
        query: searchTerm,
        groups: groups,
      },
      skip: !includePublicUsers,
    });

  const loading = usersLoading || publicUsersLoading;
  const initialValueRef = useRef(value);
  const [loadInitialUsers] = useSearchUsersLazyQuery();
  const [loadInitialPublicUsers] = useSearchPublicUsersLazyQuery();

  // On mount, query for selected users if value is set since we might need to load suspended users.
  useEffect(() => {
    const userIds = initialValueRef.current["users"] || [];
    const publicUserIds = initialValueRef.current["publicUsers"] || [];

    const loadAllUsers = async () => {
      let allInitialUsers: UserOption[] = [];

      if (userIds.length > 0) {
        const initialUsers = await loadInitialUsers({
          variables: { userIds: userIds },
        });

        if (initialUsers.data?.searchUsers) {
          allInitialUsers.push(...initialUsers.data.searchUsers);
        }
      }

      if (publicUserIds.length > 0) {
        const initialPublicUsers = await loadInitialPublicUsers({
          variables: {
            publicUserIds: publicUserIds,
          },
        });

        if (initialPublicUsers.data?.searchPublicUsers) {
          const formattedPublicUsers =
            initialPublicUsers.data.searchPublicUsers.map(publicUser => ({
              ...publicUser,
              role: { name: "Guest" },
              _userType: "guest" as const,
            }));

          allInitialUsers.push(...formattedPublicUsers);
        }
      }

      if (allInitialUsers.length > 0) {
        setSelectedUsers(allInitialUsers);
      }
    };

    void loadAllUsers();
  }, [loadInitialUsers, loadInitialPublicUsers]);

  const options = useMemo(() => {
    const allUsers = [];

    if (usersData && includeUsers) {
      allUsers.push(...usersData.searchUsers);
    }

    if (publicUsersData && includePublicUsers) {
      const formattedPublicUsers = publicUsersData.searchPublicUsers.map(
        publicUser => {
          const roleName = publicUser.groups?.[0]?.name || "Guest";

          return {
            id: publicUser.id,
            firstName: publicUser.firstName,
            lastName: publicUser.lastName,
            email: publicUser.email,
            role: { name: roleName },
            _userType: "guest",
          } as UserOption;
        }
      );

      allUsers.push(...formattedPublicUsers);
    }

    allUsers.sort((a, b) => {
      const firstNameA = a.firstName?.toLowerCase() || "";
      const firstNameB = b.firstName?.toLowerCase() || "";
      return firstNameA.localeCompare(firstNameB);
    });

    if (allUsers.length > 0) {
      const selectAllOption: UserOption = {
        id: SELECT_ALL_VALUE,
        firstName: "",
        lastName: "",
        email: "",
        role: { name: "manager" },
      };
      return [selectAllOption, ...allUsers];
    }

    return allUsers;
  }, [usersData, publicUsersData, includeUsers, includePublicUsers]);

  const handleInputChange = (newValue: string) => {
    setSearchTerm(newValue);
  };

  const handleChange = (selectedValues: MultiValue<UserOption>) => {
    if (!selectedValues) {
      setSelectedUsers([]);
      onChange(null);
      return;
    }

    const selectAllOption = selectedValues.find(
      option => option.id === SELECT_ALL_VALUE
    );
    const regularUsers = options.filter(user => user.id !== SELECT_ALL_VALUE);

    let usersToProcess: UserOption[];

    if (selectAllOption) {
      usersToProcess = regularUsers;
      setSelectedUsers(regularUsers);
    } else {
      usersToProcess = selectedValues.filter(
        option => option.id !== SELECT_ALL_VALUE
      );
      setSelectedUsers(usersToProcess);
    }

    const users = usersToProcess.filter(user => user._userType !== "guest");
    const publicUsers = usersToProcess.filter(
      user => user._userType === "guest"
    );

    const newValue = { ...value };
    newValue["users"] = users.map(user => user.id);
    newValue["publicUsers"] = publicUsers.map(publicUser => publicUser.id);

    onChange(newValue);
  };

  // We need to use CreatableSelect instead of our Select component because the graphql query returns a dynamic list of users that get filtered by the inputted searchTerm.
  // react-select will not display the selected users if they don't exist in the filtered options list, so we need to use CreatableSelect.
  // We also need to set isValidNewOption={() => false} because we don't want to allow new options to be added.
  return (
    <Wrapper
      label={label}
      required={required}
      name={name}
      error={error}
      tooltip={tooltip}
      helperText={helperText}
    >
      <CreatableSelect
        name={name}
        options={options}
        filterOption={() => {
          // Return true to always show all options. We handle filtering via GraphQL queries on the server
          // so we don't want react-select to filter options client-side.
          // This prevents the "Select All" option from disappearing when searching.
          return true;
        }}
        value={selectedUsers}
        onChange={handleChange}
        isDisabled={disabled}
        isMulti={true}
        placeholder={placeholder}
        getOptionLabel={option => formatValue(option)}
        getOptionValue={option => option.id}
        styles={{
          multiValue: (base, state) => ({
            ...base,
            ...styledCreatableSelect(state).multiValue,
          }),
          multiValueLabel: (base, state) => ({
            ...base,
            ...styledCreatableSelect(state).multiValueLabel,
          }),
          multiValueRemove: (base, state) => ({
            ...base,
            ...styledCreatableSelect(state).multiValueRemove,
          }),
          option: (base, state) => ({
            ...base,
            ...styledCreatableSelect(state).option,
          }),
        }}
        components={{
          MultiValue: props => (
            <CustomMultiValue
              {...props}
              menuIsOpen={menuIsOpen}
              maxSelectedUsersToDisplay={maxSelectedUsersToDisplay}
            />
          ),
          MultiValueRemove,
          Option: CustomOption,
          ClearIndicator,
        }}
        isClearable
        hideSelectedOptions={false}
        closeMenuOnSelect={false}
        onMenuOpen={() => setMenuIsOpen(true)}
        onMenuClose={() => setMenuIsOpen(false)}
        isLoading={loading}
        onInputChange={handleInputChange}
        isValidNewOption={() => false}
        {...props}
      />
    </Wrapper>
  );
};

export default UserSelect;
